// import hooks
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetColumns } from './hooks/useGetColumns';
import { useSelector } from 'hooks';
import { useQueryClient } from 'react-query';
import useGetHierarchyValues from './hooks/useGetHierarchyValues';

// import components
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import WithInnerLayout from 'components/shared/CRUD/components/InnerPage/WithInnerLayout';
import { LinkPlanUnitDrawer } from './components/LinkPlanUnitDrawer';
import { FilterComponent } from './components/filter/FilterComponent';
import CreatePlanUnit from './components/form/CreatePlanUnit';
import EditPlanUnit from './components/form/edit/EditPlanUnit';

// import utils
import { pipe } from 'ramda';
import {
  filterByReferenceDate,
  filterBySearch,
  filterByClusterId,
  filterPoolId,
  filterEmployerId,
  filterByEmpPoolId
} from './components/utils/filters';
import dayjs from 'dayjs';
import {
  HierarchyPlanUnit,
  createTreeHierarchy
} from './components/utils/treeHierarchy';
import {
  hasActiveFilter,
  isGroupRow,
  toggleAllGroups
} from './PlanUnits.utils';

// import types
import { FilterStateTypes, PlanUnit, PlanUnitLink } from './types';
import { DataGridActionClick } from 'components/shared/DataGrid/DataGrid.types';
import { Filters } from './components/filter/FilterComponent.types';

// import MUI DataGrid components / hooks / types
import {
  gridFilteredSortedRowIdsSelector,
  GridFilterModel,
  useGridApiRef
} from '@mui/x-data-grid-pro';

const PlanUnits = ({ isInnerLayoutOpen, setIsInnerLayoutOpen }) => {
  const { t } = useTranslation();
  const { hasClustersOrgGrant, hasPoolsOrgGrant } = useSelector(
    (state) => state.grants
  );
  const queryClient = useQueryClient();
  const [filterStates, setFilterStates] = useState<FilterStateTypes>({
    referenceDate: dayjs().startOf('day').toISOString(),
    clusterId: null,
    poolId: null,
    empPoolId: null,
    employerId: null,
    searchTerm: '',
    noEmployer: false,
    noPool: false,
    noEmployeePool: false
  });
  const hasReferenceDate = !!filterStates?.referenceDate;
  const columns = useGetColumns({ hasReferenceDate });

  const startDate = dayjs(filterStates.referenceDate).isValid()
    ? dayjs(filterStates.referenceDate).toISOString()
    : '';
  const endDate = dayjs(startDate).isValid()
    ? dayjs(startDate).add(1, 'days').toISOString()
    : '';
  const { data: rows, isFetching } = useGetHierarchyValues(startDate, endDate);

  const [linkMode, setLinkMode] = useState<PlanUnitLink>();
  const [selectedRow, setSelectedRow] = useState<PlanUnit>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<PlanUnit[] | undefined>();
  const [mergedRows, setMergedRows] = useState<
    PlanUnit[] | HierarchyPlanUnit[] | undefined
  >([]);
  const [collapse, setCollapse] = useState(0);
  const [renderKey, setRenderKey] = useState(0); // force component to re-render due to collapse state change
  const [hideDescendantCount, setHideDescendantCount] = useState(false);
  const apiRef = useGridApiRef(); // we use the api to track the built-in filter model state
  const createdBrIdRef = useRef<number | null>(null);
  const listKey = useMemo(() => {
    const viewType = hasReferenceDate ? 'tree' : 'flat';
    const refDateStr = filterStates.referenceDate ?? 'none';

    return `${renderKey}-${viewType}-${refDateStr}`;
  }, [renderKey, hasReferenceDate, filterStates.referenceDate]);

  const assignUniqueIds = (rows: HierarchyPlanUnit[]) =>
    rows.map((row, index) => ({
      ...row,
      __rowId: `${row.brId}-${row.code}-${index}`
    }));

  useEffect(() => {
    if (rows && rows.length > 0) {
      const rowsWithUniqueIds = assignUniqueIds(rows);
      setMergedRows(createTreeHierarchy(rowsWithUniqueIds));
    }
  }, [rows]);

  const toggleInnerLayout = () => {
    setIsInnerLayoutOpen(!isInnerLayoutOpen);
  };

  const onViewClick = useCallback(
    (rowData) => {
      setSelectedRow(rowData);
      setIsInnerLayoutOpen(true);
    },
    [setIsInnerLayoutOpen]
  );

  const onCreated = useCallback((brId: number) => {
    createdBrIdRef.current = brId;
  }, []);

  // Navigate to the newly created row by brId after data refetches
  useEffect(() => {
    if (createdBrIdRef.current && rows && rows.length > 0) {
      const createdRow = rows.find(
        (row) => row.brId === createdBrIdRef.current
      );
      if (createdRow) {
        createdBrIdRef.current = null;
        onViewClick(createdRow);
      }
    }
  }, [rows, onViewClick]);

  const handleOpenLinkDrawer: (
    mode: PlanUnitLink
  ) => DataGridActionClick<PlanUnit> = (mode: PlanUnitLink) => (id, row) => {
    setLinkMode(mode);
    setSelectedRow(row);
    setOpenDrawer(true);
  };

  const handleCloseLinkDrawer = () => {
    setOpenDrawer(false);
  };

  if (isInnerLayoutOpen && selectedRow) {
    return (
      <EditPlanUnit
        rowData={selectedRow}
        setIsInnerLayoutOpen={setIsInnerLayoutOpen}
        toggleInnerLayout={toggleInnerLayout}
        rows={mergedRows}
        onSaveChanges={() => {
          queryClient.invalidateQueries(['hierarchy', 'list']);
        }}
      />
    );
  }

  const handleFilterChange = async (filters: Filters) => {
    if (isFetching || !mergedRows) return mergedRows;
    const {
      referenceDate,
      search,
      clusterId,
      poolId,
      empPoolId,
      employerId,
      noEmployer,
      noPool,
      noEmployeePool
    } = filters;

    setFilterStates({
      referenceDate: filters.referenceDate,
      clusterId: filters.clusterId,
      poolId: filters.poolId,
      empPoolId: filters.empPoolId,
      employerId: filters.employerId,
      searchTerm: filters.search,
      noEmployer: filters.noEmployer,
      noPool: filters.noPool,
      noEmployeePool: filters.noEmployeePool
    });

    const filtered = await pipe(
      (data) => filterByReferenceDate(data, referenceDate),
      (data) => filterBySearch(data, search),
      (data) => filterByClusterId(data, clusterId),
      (data) => filterPoolId(data, poolId, noPool),
      (data) => filterEmployerId(data, employerId, noEmployer),
      (data) => filterByEmpPoolId(data, empPoolId, noEmployeePool)
    )(mergedRows);

    setFilteredRows(filtered);

    setCollapse(
      search ||
        clusterId ||
        poolId ||
        employerId ||
        empPoolId ||
        noPool ||
        noEmployer ||
        noEmployeePool
        ? -1
        : 0
    );
    setHideDescendantCount(!!search);
    setRenderKey((prevKey) => prevKey + 1);
  };

  const getRowClassName = (params) => {
    // we highlight the rows that match the filter criteria of the MUI built-in filter
    const filterModel = apiRef?.current?.state.filter.filterModel;
    const hasFilterValue = filterModel?.items.some(
      (item) => item.value !== undefined && item.value !== ''
    );
    const allMatchingIds = gridFilteredSortedRowIdsSelector(apiRef);
    const leafIds = allMatchingIds.filter(
      (id) => !isGroupRow(apiRef?.current?.getRowNode(id))
    );

    const isHighlighted =
      // check if any filter state has a value from our custom filter states
      Object.keys(filterStates).some((key) => {
        const value = filterStates[key];
        if (!value) return false;
        if (key === 'searchTerm') {
          return (
            params.row.name.toLowerCase().includes(value.toLowerCase()) ||
            params.row.code.toLowerCase().includes(value.toLowerCase())
          );
        }
        return false;
      }) ||
      (hasFilterValue && leafIds.includes(params.id)); // check if the row is highlighted by the MUI built-in filter

    return isHighlighted ? 'highlighted-row' : '';
  };

  const handleBuiltInFilterChange = (model: GridFilterModel) => {
    toggleAllGroups(hasActiveFilter(model), apiRef);
  };

  const needsSafeTransition =
    hasReferenceDate &&
    (isFetching || !filteredRows || filteredRows.length === 0);

  return (
    <>
      <FilterComponent
        rows={mergedRows}
        handleFilterChange={handleFilterChange}
        dependencies={[mergedRows]}
      />
      <TableCRUDLayout
        apiRef={apiRef}
        key={listKey} // Use unique key to force re-render
        getRowClassName={getRowClassName}
        editable={false}
        loading={isFetching}
        hasTreeData={hasReferenceDate && !needsSafeTransition}
        getTreeDataPath={(row) => row.hierarchy ?? []}
        onFilterModelChange={handleBuiltInFilterChange}
        getRowId={(data) => (data?.__rowId ? data?.__rowId : data?.brId)}
        rows={needsSafeTransition ? [] : filteredRows ?? []}
        onCellClick={(_, event) => event.stopPropagation()}
        columns={columns}
        collapse={collapse}
        hideDescendantCount={hideDescendantCount}
        FormComponent={CreatePlanUnit}
        additionalFormProps={{ onCreated }}
        actionsThreshold={2}
        onViewClick={(rowId, rowData) => onViewClick(rowData)}
        initialState={{
          sorting: {
            sortModel: [{ field: 'code', sort: 'asc' }]
          }
        }}
        customActions={() => [
          {
            icon: 'link',
            tooltipTitle: t('linkTo', {
              mode: `${t(PlanUnitLink.EMPLOYER)[0].toUpperCase()}${t(
                PlanUnitLink.EMPLOYER
              )
                .toLowerCase()
                .substring(1)}`
            }),
            onClick: handleOpenLinkDrawer(PlanUnitLink.EMPLOYER)
          },
          ...(hasClustersOrgGrant
            ? [
                {
                  icon: 'link',
                  tooltipTitle: t('linkTo', {
                    mode: t(PlanUnitLink.CLUSTER)
                  }),
                  onClick: handleOpenLinkDrawer(PlanUnitLink.CLUSTER)
                }
              ]
            : []),
          ...(hasPoolsOrgGrant
            ? [
                {
                  icon: 'link',
                  tooltipTitle: t('manage', { mode: t('employeePool') }),
                  onClick: handleOpenLinkDrawer(PlanUnitLink.EMPLOYEE_POOL)
                },
                {
                  icon: 'link',
                  tooltipTitle: t('manage', { mode: t('requestPool') }),
                  onClick: handleOpenLinkDrawer(PlanUnitLink.REQUEST_POOL)
                }
              ]
            : [])
        ]}
      />
      {selectedRow && (
        <LinkPlanUnitDrawer
          open={openDrawer}
          onClose={handleCloseLinkDrawer}
          selectedRow={selectedRow}
          mode={linkMode}
          hierarchy={mergedRows}
          hasReferenceDate={hasReferenceDate}
        />
      )}
    </>
  );
};

const PlanUnitsWithLayout = WithInnerLayout(PlanUnits);
export default PlanUnitsWithLayout;
